import * as React from 'react';

import {formEncodedApi} from 'nutshell-core/api';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Popover, PopoverComponent} from 'shells/popover/popover';
import {PopoverForm} from 'shells/popover-form/popover-form';
import {Body} from 'shells/typography/body';

import type {Lead as LeadFragment} from 'nutshell-graphql-types';

import {ConfirmationFormWinLead} from '../../lead/shared/components';

import {QuickbooksConnectCTA, handleCtaClick} from './quickbooks-connect-cta';

import styles from './quickbooks-popover.module.css';

type Props = {
    lead: LeadFragment;
    onCancel: () => void;
    onSuccess: () => void;
    onSubmit: () => Promise<any>;
    submitButtonText: string;
    canAccessIntegrations: boolean;
} & JSX.LibraryManagedAttributes<
    typeof PopoverComponent,
    React.ComponentProps<typeof PopoverComponent>
>;

export const QuickbooksLeadPopover = (props: Props) => {
    const [isQuickbooksConnected, setQuickbooksConnected] = React.useState<boolean>(
        NutClientConfig.integrations && NutClientConfig.integrations.quickbooksEnabled
    );
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    function getLeadInvoiceId() {
        const invoiceResource = props.lead.externalResources.find((externalResource) => {
            return externalResource.externalResourceType === 'QUICKBOOKS_INVOICE';
        });

        return invoiceResource ? invoiceResource.externalId : null;
    }

    function handleSubmit() {
        // Only allow submission if an invoice doesn't already exist
        if (!getLeadInvoiceId()) {
            setIsSubmitting(true);

            props
                .onSubmit()
                .then(() => {
                    setIsSubmitting(false);
                })
                .catch((err) => {
                    setIsSubmitting(false);
                    if (err && err.message) {
                        setErrorMessage(safelyGetGraphQLErrorMessage(err));
                    } else {
                        setErrorMessage(
                            'Sorry, something went wrong. Could not connect with Quickbooks'
                        );
                    }
                });
        }
    }

    function handleSaveComplete() {
        props.onSuccess();
    }

    if (errorMessage) {
        return (
            <Popover
                anchor={props.anchor}
                isDialog={true}
                onBlur={() => {
                    setErrorMessage(null);
                    props.onCancel();
                }}
                bodyColor='error'
            >
                {errorMessage}
            </Popover>
        );
    }

    const invoiceId = getLeadInvoiceId();

    return (
        <Popover anchor={props.anchor} onBlur={props.onCancel} location={props.location}>
            {isQuickbooksConnected ? (
                <PopoverForm
                    width={340}
                    onClose={props.onCancel}
                    title={
                        <div className={styles['logo-title']}>
                            <img
                                alt='Quickbooks'
                                src='/include/images/integration-logos/quickbooks.png'
                                height={16}
                                style={{marginRight: 8}}
                            />
                            Create invoice
                        </div>
                    }
                    onSubmit={handleSubmit}
                    saveButtonText={props.submitButtonText}
                    saveButtonDisabled={invoiceId !== null}
                    isSaving={isSubmitting}
                    onClearSaveComplete={handleSaveComplete}
                    saveButtonSavingText='Creating invoice…'
                    saveButtonSuccessText='Invoice created!'
                >
                    {invoiceId ? (
                        <React.Fragment>
                            <Body as='p'>Done! A new invoice was created in QuickBooks.</Body>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <Body as='p'>
                                Would you like to create a draft invoice in QuickBooks for this
                                lead? It won’t be sent to the customer yet.
                            </Body>
                            <ConfirmationFormWinLead isReadOnly={true} leadId={props.lead.id} />
                        </React.Fragment>
                    )}
                </PopoverForm>
            ) : (
                <QuickbooksConnectCTA
                    canAccessIntegrations={props.canAccessIntegrations}
                    onClick={() => {
                        handleCtaClick().then(() => {
                            formEncodedApi
                                .get('/integrations/status')
                                // @ts-expect-error
                                .then((res) => res.json())
                                .then((json) => {
                                    if (
                                        json.integrationsEnabled &&
                                        json.integrationsEnabled.quickbooks
                                    ) {
                                        setQuickbooksConnected(true);
                                        // For the next time the popover gets opened, before a full refresh
                                        if (NutClientConfig.integrations) {
                                            NutClientConfig.integrations.quickbooksEnabled = true;
                                        }
                                    } else {
                                        props.onCancel();
                                    }
                                })
                                .catch(() => {
                                    // Just close the popover if something goes wrong.
                                    props.onCancel();
                                });
                        });
                    }}
                />
            )}
        </Popover>
    );
};
